import React, { useState, useEffect } from 'react';
import { SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaInfoCircle, FaDownload, FaLaptop, FaMemory, FaHdd, FaMicrochip, FaFileAlt } from 'react-icons/fa';
import { moduleChangelogs, systemRequirements } from '../data/OpusChangelogsData';

const Changelog24Module1 = () => {
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [expandedSection, setExpandedSection] = useState('changes');
  
  const toggleSection = (section) => {
    setExpandedSection(section === expandedSection ? null : section);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Changelog OPUS 24 Módulo 1 | Ecosoft"
        description="Historial de cambios y mejoras de OPUS 24 Módulo 1: Presupuesto Programable."
        name="Changelog24Module1"
        type="website"
      />
      
      {/* Hero Section */}
      <section id="overview" className="relative h-[350px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-12 relative z-10">
          <div className="max-w-2xl text-white">
            <h1 className="text-4xl font-bold mb-2">OPUS 24 - Módulo 1</h1>
            <h2 className="text-2xl font-semibold mb-4">Presupuesto Programable</h2>
            <p className="text-lg mb-6">
              Consulte el historial de cambios y mejoras del Módulo 1 de OPUS 24.
            </p>
            <div className="flex gap-4">
              <Link to="/soporte/descargas" className="inline-flex items-center text-white hover:underline">
                <FaArrowRight className="mr-2 rotate-180" /> Volver al Centro de Descargas
              </Link>
              <Link to="/modulos/presupuesto-programable" className="inline-flex items-center text-white hover:underline">
                <FaInfoCircle className="mr-2" /> Ver información del módulo
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8">
            <div className="flex items-center mb-6">
              <div className="bg-[#0055B8]/10 p-3 rounded-full mr-4">
                <FaInfoCircle className="text-[#0055B8] text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#1A2B39]">Módulo 1: Presupuesto Programable</h2>
                <p className="text-gray-600">Historial de cambios y mejoras de la versión OPUS 24</p>
              </div>
            </div>
            
            <p className="mb-6 text-gray-700">
              El Módulo 1 de OPUS 24 le permite elaborar presupuestos de obra de manera rápida y sencilla. Con nuestras continuas mejoras, este módulo se mantiene a la vanguardia para satisfacer las necesidades de los profesionales de la construcción. A continuación, encontrará el detalle de las actualizaciones más recientes.
            </p>
            
            <div className="flex justify-end">
              <Link 
                to="/soporte/descargas/opus24/m1" 
                className="inline-flex items-center text-[#0055B8] hover:underline"
              >
                <FaDownload className="mr-2" /> Ir a descargas del Módulo 1
              </Link>
            </div>
          </div>
          
          {/* Changelog Section */}
          <div 
            className={`bg-white p-6 rounded-lg shadow border border-gray-200 mb-8 ${expandedSection === 'changes' ? 'ring-2 ring-[#0055B8]' : ''}`}
            onClick={() => toggleSection('changes')}
          >
            <div className="flex justify-between items-center cursor-pointer">
              <h2 className="text-2xl font-bold text-[#1A2B39]">Historial de cambios</h2>
              <span className="text-[#0055B8]">{expandedSection === 'changes' ? '−' : '+'}</span>
            </div>
          </div>
          
          {expandedSection === 'changes' && (
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8">
              <div className="space-y-10">
                {moduleChangelogs.module1.map((version, index) => (
                  <div key={index} className="border-b border-gray-200 pb-8 last:border-b-0 last:pb-0">
                    <div className="flex items-start gap-4 md:gap-8">
                      <div className="bg-[#0055B8]/10 p-3 rounded-full">
                        <FaInfoCircle className="text-[#0055B8] text-xl" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#1A2B39] mb-2">Versión {version.version}</h3>
                        <p className="text-gray-600 mb-4">Fecha: {version.date}</p>
                        
                        <h4 className="font-bold text-[#0055B8] mb-2">Cambios y mejoras:</h4>
                        <ul className="list-disc pl-6 space-y-2 text-gray-700">
                          {version.changes.map((change, changeIndex) => (
                            <li key={changeIndex}>{change}</li>
                          ))}
                        </ul>
                        
                        <div className="mt-6">
                          <Link 
                            to="/soporte/descargas/opus24/m1" 
                            className="inline-flex items-center px-4 py-2 bg-[#0055B8] text-white rounded hover:bg-[#003a80] transition-colors"
                          >
                            <FaDownload className="mr-2" /> Descargar esta versión
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* System Requirements Section */}
          <div 
            className={`bg-white p-6 rounded-lg shadow border border-gray-200 mb-8 ${expandedSection === 'requirements' ? 'ring-2 ring-[#0055B8]' : ''}`}
            onClick={() => toggleSection('requirements')}
          >
            <div className="flex justify-between items-center cursor-pointer">
              <h2 className="text-2xl font-bold text-[#1A2B39]">Requisitos del sistema</h2>
              <span className="text-[#0055B8]">{expandedSection === 'requirements' ? '−' : '+'}</span>
            </div>
          </div>
          
          {expandedSection === 'requirements' && (
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-[#1A2B39] mb-4 flex items-center">
                    <FaLaptop className="text-[#0055B8] mr-2" /> Requisitos mínimos
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <FaMicrochip className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Procesador:</span> {systemRequirements.minimum.processor}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaMemory className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Memoria RAM:</span> {systemRequirements.minimum.ram}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaHdd className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Almacenamiento:</span> {systemRequirements.minimum.storage}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaInfoCircle className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Tarjeta gráfica:</span> {systemRequirements.minimum.graphics}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaInfoCircle className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Sistema operativo:</span> {systemRequirements.minimum.os}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaInfoCircle className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Pantalla:</span> {systemRequirements.minimum.display}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaInfoCircle className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Adicional:</span> {systemRequirements.minimum.additional}
                      </div>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-[#1A2B39] mb-4 flex items-center">
                    <FaLaptop className="text-[#0055B8] mr-2" /> Requisitos recomendados
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <FaMicrochip className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Procesador:</span> {systemRequirements.recommended.processor}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaMemory className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Memoria RAM:</span> {systemRequirements.recommended.ram}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaHdd className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Almacenamiento:</span> {systemRequirements.recommended.storage}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaInfoCircle className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Tarjeta gráfica:</span> {systemRequirements.recommended.graphics}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaInfoCircle className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Sistema operativo:</span> {systemRequirements.recommended.os}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaInfoCircle className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Pantalla:</span> {systemRequirements.recommended.display}
                      </div>
                    </li>
                    <li className="flex items-start">
                      <FaInfoCircle className="text-[#0055B8] mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Adicional:</span> {systemRequirements.recommended.additional}
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-700">
                  <strong>Nota:</strong> Para un rendimiento óptimo, recomendamos utilizar la versión más reciente de Windows y mantener actualizados los controladores de su hardware. Los requisitos del sistema pueden variar según la complejidad de los proyectos que esté gestionando.
                </p>
              </div>
            </div>
          )}
          
          {/* Download Button */}
          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8 text-center">
            <h2 className="text-2xl font-bold text-[#1A2B39] mb-4">¿Listo para actualizar?</h2>
            <p className="text-gray-700 mb-6 max-w-3xl mx-auto">
              Descargue la última versión de OPUS 24 Módulo 1 y disfrute de todas las mejoras y nuevas funcionalidades que hemos desarrollado para optimizar su trabajo.
            </p>
            <div className="flex justify-center gap-4">
              <Link 
                to="/soporte/descargas/opus24/m1" 
                className="inline-flex items-center px-6 py-3 bg-[#0055B8] text-white rounded-md hover:bg-[#003a80] transition-colors text-lg font-medium"
              >
                <FaDownload className="mr-2" /> Ir al portal de descargas
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Changelog24Module1; 